import { registerSW } from 'virtual:pwa-register';
import { setupIonicReact } from '@ionic/react';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from '~/App';
import { invalidateOnPush } from '~/push/invalidation';
import { queryPersistOptions } from '~/queryPersistence';
import { STORAGE_KEYS } from '~/storage-keys';

/* Ionic CSS — ordem importa. */
import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

import '~/theme/variables.css';
import '~/theme/tailwind.css';

setupIonicReact({
  mode: 'md',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 24 * 60 * 60 * 1_000, // 24h — necessário pra cache sobreviver à reidratação
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      throwOnError: true,
    },
  },
});

// Persistência: serializa cache do TanStack Query no localStorage. O `buster` é a versão do
// `package.json` (`~/queryPersistence`): subir a versão descarta o cache gravado pela anterior.
const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: STORAGE_KEYS.queryCache,
  throttleTime: 1_000,
});
const persistOptions = queryPersistOptions(persister);

// Registra o service worker (produção); o vite-plugin-pwa cuida de noop em dev.
// `autoUpdate` aplica novas versões silenciosamente — sem prompt; é apropriado
// pra cliente final que não decide sobre versão de PWA.
registerSW({ immediate: true });

// O SW posta um recado quando recebe push do backend: `INVOICE_PUSH` para uma nota,
// `DOCUMENT_PUSH` para um post do feed e `GUIDE_PUSH` para um lembrete de guias. Aqui invalidamos
// as queries do que mudou (`INVALIDATED_BY_PUSH`, em `~/push/invalidation`), fazendo a UI buscar o
// estado atualizado no próximo refetch (em vez de esperar o polling de 2s).
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent<unknown>) => {
    void invalidateOnPush(queryClient, event.data);
  });
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Elemento #root não encontrado no index.html');
}

createRoot(container).render(
  <StrictMode>
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <App />
    </PersistQueryClientProvider>
  </StrictMode>,
);
